import { Select } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { Ref, ChangeEvent } from 'react';
import { S } from './StyledFormComponents';
import type { SurveyAnswerWithNextIndicator } from '../../types';
import type { SurveyQuestion as SurveyQuestionModel } from '../../api/survey-model';

interface Props {
  downgradeReasonOptions?: SurveyAnswerWithNextIndicator[];
  downgradeExplanations?: SurveyQuestionModel[];
  inputRef: Ref<HTMLSelectElement>;
  onChange: (selectedExplanationsGroup: SurveyQuestionModel | undefined) => void;
  error?: string;
  isInvalid: boolean;
  isDisabled: boolean;
}

export function DowngradeReasonQuestion({
  inputRef,
  onChange,
  downgradeExplanations,
  downgradeReasonOptions,
  error,
  isInvalid,
  isDisabled,
}: Props): JSX.Element {
  const { t } = useTranslationWithPrefix('checkout:churn.surveyv2');

  const onOptionChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedDowngradeReason = e.target.value;

    const downgradeReasonObj = downgradeReasonOptions?.find(
      (reason) => reason.text === selectedDowngradeReason,
    );

    // find the correct group of explanations based on the selected downgrade reason
    const selectedExplanationsGroup = downgradeExplanations?.find(
      (explanations) => explanations.uuid === downgradeReasonObj?.nextForAnswer,
    );

    // update the selection on the main form
    onChange(selectedExplanationsGroup);
  };

  return (
    <S.Field
      labelText={t('label-downgrade-reason')}
      invalidText={error}
      inputId="downgrade-reason-select"
      size="l"
      isInvalid={isInvalid}
      isRequired
    >
      <Select
        id="downgrade-reason-select"
        defaultValue=""
        name="downgradeReason"
        ref={inputRef}
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        onChange={onOptionChange}
      >
        <option value="" key="please-select-one">
          {t('placeholder-downgrade-reason')}
        </option>
        {downgradeReasonOptions?.map(({ text }) => (
          <option value={text} key={text}>
            {text}
          </option>
        ))}
      </Select>
    </S.Field>
  );
}
